import { defineEntityContract, EntityContract, type EntityStateEvents } from 'shared/channels/entity';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import { mapRecordValues } from 'shared/utils/object';
import { EditableFieldValuesEntityName } from './fields';
import type { FieldValueModel } from './fieldValueModel';

interface FieldValueContractDefinition {
  /** Key of the dictionary's contract on the channel bus. */
  key: string;
  /** Broadcast with the whole collection after each sync. */
  collectionEvent: DomainMessagesTypes;
}

/** Contract key and collection event of every field value dictionary; shared by the page and the worker. */
export const fieldValueContractDefinitions: Record<EditableFieldValuesEntityName, FieldValueContractDefinition> = {
  [EditableFieldValuesEntityName.workTypes]: {
    key: 'worktypeChannel',
    collectionEvent: DomainMessagesTypes.allWorktypes,
  },
  [EditableFieldValuesEntityName.hazardCategory]: {
    key: 'hazardCategoryChannel',
    collectionEvent: DomainMessagesTypes.allHazardCategories,
  },
  [EditableFieldValuesEntityName.environmentalAspect]: {
    key: 'environmentalAspectChannel',
    collectionEvent: DomainMessagesTypes.allEnvironmentalAspects,
  },
  [EditableFieldValuesEntityName.proposedCorrectiveAction]: {
    key: 'correctiveActionTypeChannel',
    collectionEvent: DomainMessagesTypes.allCorrectiveActionTypes,
  },
  [EditableFieldValuesEntityName.delayReason]: {
    key: 'delayReasonChannel',
    collectionEvent: DomainMessagesTypes.allDelayReasons,
  },
  [EditableFieldValuesEntityName.issueVerificationItem]: {
    key: 'issueVerificationItemChannel',
    collectionEvent: DomainMessagesTypes.allIssueVerificationItems,
  },
  [EditableFieldValuesEntityName.rejectionReason]: {
    key: 'rejectionReasonChannel',
    collectionEvent: DomainMessagesTypes.allRejectionReasons,
  },
  [EditableFieldValuesEntityName.rootCauses]: {
    key: 'rootCauseChannel',
    collectionEvent: DomainMessagesTypes.allRootCauses,
  },
  [EditableFieldValuesEntityName.effect]: {
    key: 'effectChannel',
    collectionEvent: DomainMessagesTypes.allEffects,
  },
  [EditableFieldValuesEntityName.circumstances]: {
    key: 'circumstanceChannel',
    collectionEvent: DomainMessagesTypes.allCircumstances,
  },
  [EditableFieldValuesEntityName.impact]: {
    key: 'impactChannel',
    collectionEvent: DomainMessagesTypes.allImpacts,
  },
  [EditableFieldValuesEntityName.decisionToImposeFine]: {
    key: 'decisionToImposeFineChannel',
    collectionEvent: DomainMessagesTypes.allDecisionsToImposeFine,
  },
  [EditableFieldValuesEntityName.spilledSubstance]: {
    key: 'spilledSubstanceChannel',
    collectionEvent: DomainMessagesTypes.allSpilledSubstances,
  },
  [EditableFieldValuesEntityName.priority]: {
    key: 'priorityChannel',
    collectionEvent: DomainMessagesTypes.allPriorities,
  },
  [EditableFieldValuesEntityName.projectStage]: {
    key: 'projectStageChannel',
    collectionEvent: DomainMessagesTypes.allProjectStages,
  },
  [EditableFieldValuesEntityName.employmentType]: {
    key: 'employmentTypeChannel',
    collectionEvent: DomainMessagesTypes.allEmploymentTypes,
  },
  [EditableFieldValuesEntityName.injuryType]: {
    key: 'injuryTypeChannel',
    collectionEvent: DomainMessagesTypes.allInjuryTypes,
  },
  [EditableFieldValuesEntityName.injuredBodyPart]: {
    key: 'injuredBodyPartChannel',
    collectionEvent: DomainMessagesTypes.allInjuredBodyParts,
  },
  [EditableFieldValuesEntityName.requirements]: {
    key: 'requirementsChannel',
    collectionEvent: DomainMessagesTypes.allRequirements,
  },
  [EditableFieldValuesEntityName.installationBasis]: {
    key: 'installationBasisChannel',
    collectionEvent: DomainMessagesTypes.allInstallationBasis,
  },
  [EditableFieldValuesEntityName.constructionProductDocuments]: {
    key: 'constructionProductDocumentsChannel',
    collectionEvent: DomainMessagesTypes.allConstructionProductDocuments,
  },
  [EditableFieldValuesEntityName.deviceDocuments]: {
    key: 'deviceDocumentsChannel',
    collectionEvent: DomainMessagesTypes.allDeviceDocuments,
  },
  [EditableFieldValuesEntityName.additionalDocuments]: {
    key: 'additionalDocumentsChannel',
    collectionEvent: DomainMessagesTypes.allAdditionalDocuments,
  },
};

/**
 * Every dictionary. Not `editableFieldValuesEntityNames`: that enum's members
 * take values of another enum, so `Object.values` also returns reverse-mapped
 * member names (26 entries instead of 23).
 */
export const fieldValueEntityNames = Object.keys(
  fieldValueContractDefinitions
) as EditableFieldValuesEntityName[];

/**
 * Events of a dictionary channel. The collection event (`all*`) differs per
 * dictionary, so the client reads it at runtime (`readCollection`).
 */
export type FieldValueEvents = EntityStateEvents & Partial<Record<DomainMessagesTypes, unknown>>;

export type FieldValueContract = EntityContract<FieldValueModel, FieldValueEvents>;

export const fieldValueContracts: Record<EditableFieldValuesEntityName, FieldValueContract> =
  mapRecordValues(fieldValueContractDefinitions, ({ key, collectionEvent }) =>
    defineEntityContract<FieldValueModel, FieldValueEvents>(key, { collectionEvent })
  );
